import { Box, Grid, Theme, SxProps } from '@mui/material';

import { InviteAutocompleteInput } from './InviteAutocompleteInput';
import { InviteInput } from './InviteInput';
import { InviteMembersButtonGroup } from './InviteMembersButtonGroup';
import { useInvitesFormField } from '../hooks/useInvitesForm';

const ROLE_OPTIONS = [
  { label: 'Parent/Guardian', value: 'parent/guardian' },
  { label: 'Student', value: 'student' },
];

type InviteMembersFormFieldProps = Readonly<{
  showSuccessMessage: boolean;
  setShowSuccessMessage: (show: boolean) => void;
  showErrorMessage: boolean;
  setShowErrorMessage: (showErrorMessage: boolean) => void;
  isLoading: boolean;
  availableSeats: number;
}>;

export const InviteMembersFormField = ({
  showSuccessMessage,
  setShowSuccessMessage,
  showErrorMessage,
  setShowErrorMessage,
  isLoading,
  availableSeats,
}: InviteMembersFormFieldProps) => {
  const { fields, addNewInvite, isInviteGroupEmpty } = useInvitesFormField();

  const remainingSeats = availableSeats - fields.length;

  return (
    <Box sx={boxSx}>
      {fields.map((field, index) => (
        <Grid container spacing={1.5} key={field.id}>
          <Grid item xs={12} sm>
            <InviteInput
              name={`invites.${index}.first_name`}
              placeholder="Name"
              type="text"
              rules={{
                validate: (value) => {
                  if (isInviteGroupEmpty('first_name', value || '', index)) {
                    return true; // Skip validation if group is empty
                  }
                  if (!value?.trim()) {
                    return 'Name is required';
                  }

                  return true;
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={7.25}>
            <InviteInput
              name={`invites.${index}.email`}
              placeholder="Email"
              type="email"
              rules={{
                validate: (value) => {
                  if (isInviteGroupEmpty('email', value || '', index)) {
                    return true; // Skip validation if group is empty
                  }
                  if (!value?.trim()) {
                    return 'Email is required';
                  }
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                    return 'Invalid email address';
                  }

                  return true;
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm>
            <InviteAutocompleteInput
              name={`invites.${index}.role`}
              value={ROLE_OPTIONS.find((option) => option.value === field.role) || undefined}
              defaultValue={undefined}
              placeholder="Role"
              options={ROLE_OPTIONS}
              rules={{
                validate: (value) => {
                  if (isInviteGroupEmpty('role', value || '', index)) {
                    return true; // Skip validation if group is empty
                  }
                  if (!value?.trim()) {
                    return 'Role is required';
                  }

                  return true;
                },
              }}
            />
          </Grid>
        </Grid>
      ))}
      <InviteMembersButtonGroup
        showSuccessMessage={showSuccessMessage}
        setShowSuccessMessage={setShowSuccessMessage}
        showErrorMessage={showErrorMessage}
        setShowErrorMessage={setShowErrorMessage}
        addNewInvite={addNewInvite}
        isLoading={isLoading}
        remainingSeats={remainingSeats}
      />
    </Box>
  );
};

const boxSx: SxProps<Theme> = ({ backrs }) => ({
  '& > .MuiGrid-container:not(:first-of-type)': {
    marginTop: backrs.spacing['spacing-md'],
  },
});
